/**
 * FO-058 (batch-18) Deel B — gedeelde types/Zod-schemas voor de geautomatiseerde
 * placeholder-mails. Eén bron van waarheid voor de API-validatie én de
 * backoffice-editor (per-placeholder bron + parameters).
 */
import { z } from 'zod'
import {
  automatiseringOntvangerInputSchema,
  ONDERHOUD_CADANS,
  vereisFrequentieParams,
  WEEK_PARITEIT,
  type AutomatiseringOntvangerDTO,
  type FrequentieVelden,
  type OnderhoudCadans,
  type OntvangerResultaatDTO,
  type WeekPariteit,
} from './onderhoud-controle'

// ─── Enums (spiegelen de Prisma-enums) ───────────────────────────────────────

export const MAIL_PLACEHOLDER_BRON = ['VASTE_TEKST', 'KEUZELIJST', 'APP_QUERY'] as const
export type MailPlaceholderBron = (typeof MAIL_PLACEHOLDER_BRON)[number]

export const MAIL_VERZEND_MODUS = ['AUTO', 'CONCEPT_EERST'] as const
export type MailVerzendModus = (typeof MAIL_VERZEND_MODUS)[number]

/**
 * App-query-bronnen (resolver-keys). Houd in sync met het resolver-register in
 * `apps/api/src/services/mail-automatisering/resolver-register.ts`.
 */
export const MAIL_QUERY_KEY = [
  'geplande_monteurs', // geplande monteurs op [weekdag] in week +N bij [klant]
  'datum_weekdag', // datum van [weekdag] week +N
  'klantnaam', // naam van [klant]
  'contactpersoon', // contactpersoon van [klant]
  // FO-148 (batch-29) — wekelijkse dekkingsmail (doelweek = ISO-week +1).
  'dekking_volgende_week', // volledig dekkingsrapport (rooster-tekorten + onderhoudscontrole)
  'weeknummer', // ISO-weeknummer van de doelweek (voor het onderwerp)
  'dekking_samenvatting', // korte samenvatting van de doelweek (voor het onderwerp)
] as const
export type MailQueryKey = (typeof MAIL_QUERY_KEY)[number]

export const MAIL_QUERY_LABELS: Record<MailQueryKey, string> = {
  geplande_monteurs: 'Geplande monteurs (weekdag + week + klant)',
  datum_weekdag: 'Datum van een weekdag (week)',
  klantnaam: 'Naam van de klant',
  contactpersoon: 'Contactpersoon van de klant',
  dekking_volgende_week: 'Dekkingsrapport volgende week (tekorten + onderhoud)',
  weeknummer: 'Weeknummer van volgende week',
  dekking_samenvatting: 'Dekking-samenvatting volgende week (onderwerp)',
}

/** FO-148 — dekking-placeholders waarop de twee rapport-opties van toepassing zijn. */
export const DEKKING_QUERY_KEYS: readonly MailQueryKey[] = [
  'dekking_volgende_week',
  'dekking_samenvatting',
] as const

// ─── Placeholder-config ──────────────────────────────────────────────────────

export const mailPlaceholderParamsSchema = z.object({
  weekdag: z.number().int().min(1).max(7).optional(),
  weekOffset: z.number().int().min(0).max(8).optional(),
  clientId: z.string().min(1).optional(),
  // FO-148 (batch-29) — opties voor de dekking-placeholders (default allebei uit):
  // TE_VEEL (overbezetting) als extra sectie meenemen.
  metTeVeel: z.boolean().optional(),
  // Alleen versturen bij tekorten: bij nul tekorten geeft de resolver het
  // "ontbreekt"-signaal af zodat de bestaande blokkeer-flow de verzending overslaat.
  alleenBijTekorten: z.boolean().optional(),
})
export type MailPlaceholderParams = z.infer<typeof mailPlaceholderParamsSchema>

/** Per placeholder-token een bron + parameters. */
export const mailPlaceholderConfigSchema = z.discriminatedUnion('bron', [
  z.object({
    bron: z.literal('VASTE_TEKST'),
    vasteTekst: z.string().default(''),
    verplicht: z.boolean().default(false),
  }),
  z.object({
    bron: z.literal('KEUZELIJST'),
    opties: z.array(z.string()).default([]),
    gekozenOptie: z.string().default(''),
    verplicht: z.boolean().default(false),
  }),
  z.object({
    bron: z.literal('APP_QUERY'),
    queryKey: z.enum(MAIL_QUERY_KEY),
    params: mailPlaceholderParamsSchema.default({}),
    // App-query-placeholders zijn standaard verplicht: ontbrekende data ->
    // melding, geen halve mail.
    verplicht: z.boolean().default(true),
  }),
])
export type MailPlaceholderConfig = z.infer<typeof mailPlaceholderConfigSchema>

/** Map van token-naam (zonder accolades) -> config. */
export const mailPlaceholderConfigMapSchema = z.record(z.string(), mailPlaceholderConfigSchema)
export type MailPlaceholderConfigMap = z.infer<typeof mailPlaceholderConfigMapSchema>

/** Resultaat van een placeholder-resolutie/preview (API -> editor). */
export interface MailPreviewResultaat {
  onderwerp: string
  body: string
  /** Verplichte placeholders die niet ingevuld konden worden. */
  ontbrekend: string[]
  /**
   * FO-148 - placeholders die een bewuste overslaan-run vroegen (bv.
   * `alleenBijTekorten` zonder tekorten). De send-job maakt dan een stille
   * OVERGESLAGEN-run (geen melding, geen verzending); ontbrekend wint.
   */
  overgeslagen?: string[]
}

// ─── Request-schemas (create/update/preview/verzend) ──────────────────────────

const datumIso = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (jjjj-mm-dd)')

export const createMailAutomatiseringSchema = z
  .object({
    naam: z.string().min(1, 'Naam is verplicht').max(120),
    onderwerp: z.string().min(1, 'Onderwerp is verplicht').max(300),
    body: z.string().min(1, 'Body is verplicht'),
    placeholderConfig: mailPlaceholderConfigMapSchema.default({}),
    // FO-311 — generiek ontvangers-blok (was: kale e-mailadressen).
    ontvangers: z.array(automatiseringOntvangerInputSchema).default([]),
    weekdag: z.number().int().min(1, 'Weekdag moet 1-7 zijn').max(7, 'Weekdag moet 1-7 zijn'),
    cadans: z.enum(ONDERHOUD_CADANS).default('WEKELIJKS'),
    // FO-311 — frequentie zonder ankerweek: "eerstvolgende keer" + parameters.
    eerstvolgendeKeer: datumIso.nullable().optional(),
    intervalWeken: z.number().int().min(2).max(12).nullable().optional(),
    weekPariteit: z.enum(WEEK_PARITEIT).nullable().optional(),
    weekSet: z.array(z.number().int().min(1).max(53)).max(53).optional(),
    klantAutomatischVersturen: z.boolean().default(false),
    verzendModus: z.enum(MAIL_VERZEND_MODUS).default('CONCEPT_EERST'),
    actief: z.boolean().default(true),
  })
  .superRefine((v, ctx) => vereisFrequentieParams(v as FrequentieVelden, ctx))
export type CreateMailAutomatiseringInput = z.infer<typeof createMailAutomatiseringSchema>

// Expliciet all-optioneel (geen `.partial()`: Zod 4 behoudt defaults -> PATCH-reset).
export const updateMailAutomatiseringSchema = z
  .object({
    naam: z.string().min(1).max(120).optional(),
    onderwerp: z.string().min(1).max(300).optional(),
    body: z.string().min(1).optional(),
    placeholderConfig: mailPlaceholderConfigMapSchema.optional(),
    ontvangers: z.array(automatiseringOntvangerInputSchema).optional(),
    weekdag: z.number().int().min(1).max(7).optional(),
    cadans: z.enum(ONDERHOUD_CADANS).optional(),
    eerstvolgendeKeer: datumIso.nullable().optional(),
    intervalWeken: z.number().int().min(2).max(12).nullable().optional(),
    weekPariteit: z.enum(WEEK_PARITEIT).nullable().optional(),
    weekSet: z.array(z.number().int().min(1).max(53)).max(53).optional(),
    klantAutomatischVersturen: z.boolean().optional(),
    verzendModus: z.enum(MAIL_VERZEND_MODUS).optional(),
    actief: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.cadans !== undefined) vereisFrequentieParams(v as FrequentieVelden, ctx)
  })
export type UpdateMailAutomatiseringInput = z.infer<typeof updateMailAutomatiseringSchema>

/** "Genereer voorbeeld nu" — preview van een (mogelijk nog niet opgeslagen) sjabloon. */
export const previewMailAutomatiseringSchema = z.object({
  onderwerp: z.string().default(''),
  body: z.string().default(''),
  placeholderConfig: mailPlaceholderConfigMapSchema.default({}),
})
export type PreviewMailAutomatiseringInput = z.infer<typeof previewMailAutomatiseringSchema>

// ─── DTO's ────────────────────────────────────────────────────────────────────

export const MAIL_RUN_STATUS = [
  'VERSTUURD',
  'CONCEPT',
  'GEBLOKKEERD_ONTBREKENDE_DATA',
  // FO-148 - bewuste "alleen bij tekorten"-skip: stille run zonder melding.
  'OVERGESLAGEN',
] as const
export type MailRunStatus = (typeof MAIL_RUN_STATUS)[number]

export interface MailAutomatiseringRunDTO {
  id: string
  periodeKey: string
  status: MailRunStatus
  onderwerp: string | null
  body: string | null
  ontvangers: string[]
  ontbrekendePlaceholders: string[]
  /** FO-311 — resultaat per ontvanger x kanaal. */
  ontvangerResultaten: OntvangerResultaatDTO[]
  verstuurdAt: string | null
  createdAt: string
}

export interface MailAutomatiseringDTO {
  id: string
  naam: string
  onderwerp: string
  body: string
  placeholderConfig: MailPlaceholderConfigMap
  /** FO-311 — generiek ontvangers-blok (was: kale e-mailadressen). */
  ontvangers: AutomatiseringOntvangerDTO[]
  weekdag: number
  cadans: OnderhoudCadans
  /** Interne, afgeleide waarden (weergave). */
  ankerDatum: string | null
  intervalWeken: number | null
  nDeWeekdag: number | null
  weekPariteit: WeekPariteit | null
  weekSet: number[]
  /** Server-berekende eerstvolgende keer (voor het terugvullen van de DatePicker). */
  eerstvolgendeKeer: string | null
  klantAutomatischVersturen: boolean
  verzendModus: MailVerzendModus
  actief: boolean
  laatsteRun: MailAutomatiseringRunDTO | null
}
